import React, { useEffect, useRef, useState } from 'react';
import { styles } from '../../../utils/styles';
import OverlayIcon from 'Components/GMS/Rules/Components/OverlayIcon';
import PopUp from '../../PopUp/PopUp';
import AddAttributesPopup from '../../PopUp/AddAttributesPopup';
import {SortableAttribute, SortConfig} from "../../../types/sortconfig.types";
import { CollapsibleSectionHeader } from './components/CollapsibleSectionHeader';
import { imageIconUploadAPI } from '../../../services/ApiHelper';

type CategoryDetailsProps = {
    id?: string;
    projectId: string;
    modelId: string;
    isExpanded: boolean;
    onToggleExpanded: () => void;
    label: string;
    showTitle: boolean;
    titleText: string;
    showCategoryDescription: boolean;
    descriptionText: string;
    showThumbnail: boolean;
    imageDisplay: 'Fit' | 'Fill' | 'Stretch';
    categoryImageUrl?: string;
    showSortByAttributes: boolean;
    sortConfig?: SortConfig;
    showInStock: boolean;
    inStockText: string;
    selectByDefault: boolean;
    hideIfNoProducts: boolean;
    attributes: any[];
    onConfigChange: (key: string, value: any) => void;
}

export const CategoryDetails = ({ projectId, modelId, isExpanded, onToggleExpanded, label, showTitle, titleText, showCategoryDescription, descriptionText, showThumbnail, imageDisplay, categoryImageUrl, showSortByAttributes, sortConfig, showInStock, inStockText, selectByDefault, hideIfNoProducts, attributes, onConfigChange }: CategoryDetailsProps) => {
    const [showAttributeModal, setShowAttributeModal] = useState(false);
    const descriptionRef = useRef<HTMLDivElement>(null);

    // Debouncing for text inputs
    const titleDebounceRef = useRef<NodeJS.Timeout>();
    const inStockDebounceRef = useRef<NodeJS.Timeout>();

    // Local state for immediate UI feedback
    const [localTitleText, setLocalTitleText] = useState(titleText);
    const [localInStockText, setLocalInStockText] = useState(inStockText);

    // Image upload state
    const [uploadMethod, setUploadMethod] = useState<'file' | 'url'>('file');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [imageUrl, setImageUrl] = useState(categoryImageUrl || '');
    const [uploadError, setUploadError] = useState<string>('');
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Sync local state when props change externally
    useEffect(() => {
        setLocalTitleText(titleText);
    }, [titleText]);

    useEffect(() => {
        setLocalInStockText(inStockText);
    }, [inStockText]);

    useEffect(() => {
        if (categoryImageUrl) {
            setImageUrl(categoryImageUrl);
        }
    }, [categoryImageUrl]);

    // Cleanup timers on unmount
    const errorTimeoutRef = useRef<NodeJS.Timeout>();
    useEffect(() => {
        return () => {
            if (titleDebounceRef.current) {
                clearTimeout(titleDebounceRef.current);
            }
            if (inStockDebounceRef.current) {
                clearTimeout(inStockDebounceRef.current);
            }
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current);
            }
        };
    }, []);

    // Debounced handler for titleText
    const handleDebouncedTitleChange = (value: string) => {
        setLocalTitleText(value);
        if (titleDebounceRef.current) {
            clearTimeout(titleDebounceRef.current);
        }
        titleDebounceRef.current = setTimeout(() => {
            onConfigChange('titleText', value);
        }, 500);
    };

    // Image upload handlers
    const validateImageFile = (file: File): { valid: boolean; error?: string } => {
        const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
        const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

        if (file.size > MAX_FILE_SIZE) {
            return { valid: false, error: 'File size must be less than 5MB' };
        }

        if (!ALLOWED_TYPES.includes(file.type)) {
            return { valid: false, error: 'Only JPEG, PNG, WebP, and GIF formats are allowed' };
        }

        return { valid: true };
    };

    const showError = (message: string) => {
        setUploadError(message);
        if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
        }
        errorTimeoutRef.current = setTimeout(() => {
            setUploadError('');
        }, 2500);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const validation = validateImageFile(file);
        if (!validation.valid) {
            showError(validation.error || 'Invalid file');
            return;
        }

        setSelectedFile(file);
        handleFileUpload(file);
    };

    const handleFileUpload = async (file: File) => {
        setIsUploading(true);
        setUploadError('');

        try {
            const response = await imageIconUploadAPI(projectId, modelId, file, 'category');

            console.log('Upload response:', response);

            if (!response) {
                throw new Error('Upload failed - no response');
            }

            let uploadedUrl = response.imageUrl;

            if (!uploadedUrl && response.data) {
                uploadedUrl = response.data.imageUrl;
            }


            if (!uploadedUrl) {
                console.error('No URL in response:', response);
                throw new Error('No URL returned from server');
            }

            setImageUrl(uploadedUrl?.data?.imageUrl);
            onConfigChange('categoryImageUrl', uploadedUrl);
            setSelectedFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (error) {
            console.error('Image upload error:', error);
            showError('Failed to upload image. Please try again.');
            setSelectedFile(null);
        } finally {
            setIsUploading(false);
        }
    };

    const handleUrlInput = (value: string) => {
        setImageUrl(value);
        if (value.trim()) {
            onConfigChange('categoryImageUrl', value);
        }
    };

    const removeImage = () => {
        setImageUrl('');
        setSelectedFile(null);
        setUploadError('');
        onConfigChange('categoryImageUrl', '');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Debounced handler for inStockText
    const handleDebouncedInStockChange = (value: string) => {
        setLocalInStockText(value);
        if (inStockDebounceRef.current) {
            clearTimeout(inStockDebounceRef.current);
        }
        inStockDebounceRef.current = setTimeout(() => {
            onConfigChange('inStockText', value);
        }, 500);
    };

    const handleDescriptionFormat = (command: string) => {
        if (descriptionRef.current) {
            descriptionRef.current.focus();
            
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                
                document.execCommand(command, false, '');
                
                descriptionRef.current.focus();
                
                try {
                    selection.removeAllRanges();
                    selection.addRange(range);
                } catch (e) {
                    console.log('Range restoration failed, keeping focus');
                }
            } else {
                document.execCommand(command, false, '');
                descriptionRef.current.focus();
            }
        }
    };

    const handleAddAttributes = () => {
        setShowAttributeModal(true);
    };

    const handleSaveAttributes = (validAttributes: SortableAttribute[]) => {
        const defaultOptions = sortConfig?.defaultSortOptions || [
            { id: 'default_recommended', label: 'Recommended', direction: 'desc' },
            { id: 'default_price_asc', label: 'Price (Low to High)', field: 'min_ad_price', direction: 'asc' },
            { id: 'default_price_desc', label: 'Price (High to Low)', field: 'min_ad_price', direction: 'desc' },
            { id: 'default_name_asc', label: 'Alphabetical (A to Z)', field: 'name', direction: 'asc' },
            { id: 'default_name_desc', label: 'Alphabetical (Z to A)', field: 'name', direction: 'desc' }
        ];

        const updatedSortConfig: SortConfig = {
            defaultSortOptions: defaultOptions,
            sortableAttributes: validAttributes,
            selectedDefaultSortId: sortConfig?.selectedDefaultSortId || 'default_recommended'
        };

        onConfigChange('sortConfig', updatedSortConfig);

        setShowAttributeModal(false);
    };

    useEffect(() => {
        if (descriptionRef.current && descriptionRef.current.innerHTML !== descriptionText) {
            descriptionRef.current.innerHTML = descriptionText;
        }
    }, [descriptionText]);
    
    const handleDescriptionChange = (e: React.FormEvent<HTMLDivElement>) => {
        const htmlContent = e.currentTarget.innerHTML;
        onConfigChange('descriptionText', htmlContent);
    };

    const effectiveSortableAttributes = sortConfig?.sortableAttributes || [];

    return (
        <div className="">
            <div className="tw-border-b tw-border-[#D1D1D6]">
                <CollapsibleSectionHeader
                    title="Category Details"
                    isExpanded={isExpanded}
                    onToggle={onToggleExpanded}
                />
            </div>

            {/* Expanded Category Details Content */}
            {isExpanded && (
                <div className=" tw-pb-4">
                    {/* Label */}
                    <div className={styles.section}>
                        <label className={styles.inputLabel}>
                            Label
                        </label>
                        <input
                            type="text"
                            value={label}
                            disabled
                            className="tw-w-full tw-border tw-text-sm tw-text-[#172B4D] tw-font-medium tw-border-[#D1D1D6] tw-rounded tw-px-3 tw-py-2 tw-bg-[#F4F5F7] tw-cursor-not-allowed"
                        />
                    </div>
                    
                    {/* Show Title */}
                    <div className={styles.section}>
                        <div className={styles.checkboxContainer}>
                            <input
                                type="checkbox"
                                checked={showTitle}
                                onChange={(e) => onConfigChange('showTitle', e.target.checked)}
                                className={styles.checkboxInput}
                            />
                            <label className={styles.checkboxLabel}>{`Show 'Title'`}</label>
                        </div>
                        
                        {showTitle && (
                            <div className={styles.nestedContainer}>
                                <div className={styles.nestedInputContainer}>
                                    <input
                                        type="text"
                                        value={localTitleText}
                                        onChange={(e) => handleDebouncedTitleChange(e.target.value)}
                                        className={styles.input}
                                        placeholder="Enter title text"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* Show Category Description */}
                    <div className={styles.section}>
                        <div className={styles.checkboxContainer}>
                            <input
                                type="checkbox"
                                checked={showCategoryDescription}
                                onChange={(e) => onConfigChange('showCategoryDescription', e.target.checked)}
                                className={styles.checkboxInput}
                            />
                            <label className={styles.checkboxLabel}>{`Show 'Category Description'`}</label>
                        </div>
                        
                        {showCategoryDescription && (
                            <div className={styles.nestedContainer}>
                                <div className={styles.nestedInputContainer}>
                                    {/* Bold and Italic buttons */}
                                    <div className="tw-flex tw-gap-1 tw-mb-2">
                                        <button 
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleDescriptionFormat('bold');
                                            }}
                                            className="tw-w-8 tw-h-8 tw-border tw-border-[#D1D1D6] tw-rounded tw-flex tw-items-center tw-justify-center tw-bg-white hover:tw-bg-gray-50 active:tw-bg-gray-100"
                                        >
                                            <span className="tw-font-extrabold tw-text-sm">B</span>
                                        </button>
                                        <button 
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleDescriptionFormat('italic');
                                            }}
                                            className="tw-w-8 tw-h-8 tw-border tw-border-[#D1D1D6] tw-rounded tw-flex tw-items-center tw-justify-center tw-bg-white hover:tw-bg-gray-50 active:tw-bg-gray-100"
                                        >
                                            <span className="tw-italic tw-text-sm tw-font-extrabold">I</span>
                                        </button>
                                    </div>
                                    
                                    {/* Rich text editor */}
                                    <div
                                        ref={descriptionRef}
                                        contentEditable
                                        suppressContentEditableWarning={true}
                                        onInput={handleDescriptionChange}
                                        onBlur={handleDescriptionChange}
                                        className={`${styles.input} tw-min-h-[80px] tw-p-3`}
                                        style={{ wordWrap: 'break-word' }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* Show Thumbnail */}
                    <div className={styles.section}>
                        <div className={styles.checkboxContainer}>
                            <input
                                type="checkbox"
                                checked={showThumbnail}
                                onChange={(e) => onConfigChange('showThumbnail', e.target.checked)}
                                className={styles.checkboxInput}
                            />
                            <label className={styles.checkboxLabel}>Show 'Thumbnail'</label>
                        </div>
                        
                        {showThumbnail && (
                            <div className={styles.nestedContainer}>
                                <div className={styles.nestedInputContainer}>
                                    <label className={styles.inputLabel}>Image Display</label>
                                    <select
                                        value={imageDisplay}
                                        onChange={(e) => onConfigChange('imageDisplay', e.target.value)}
                                        className={styles.input}
                                    >
                                        <option value="Fit">Fit</option>
                                        <option value="Fill">Fill</option>
                                        <option value="Stretch">Stretch</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Category Image Upload */}
                    {showThumbnail && (
                        <div className={styles.section}>
                            <div className={styles.nestedContainer}>
                                <label className={styles.inputLabel}>Category Image</label>

                                {/* Upload Method Toggle */}
                                <div className="tw-flex tw-gap-2 tw-mb-3">
                                    <button
                                        onClick={() => setUploadMethod('file')}
                                        className={`tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded tw-border tw-transition-colors ${
                                            uploadMethod === 'file'
                                                ? 'tw-bg-blue-600 tw-text-white tw-border-blue-600'
                                                : 'tw-bg-white tw-text-gray-700 tw-border-gray-300 hover:tw-border-gray-400'
                                        }`}
                                        disabled={isUploading}
                                    >
                                        Upload File
                                    </button>
                                    <button
                                        onClick={() => setUploadMethod('url')}
                                        className={`tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded tw-border tw-transition-colors ${
                                            uploadMethod === 'url'
                                                ? 'tw-bg-blue-600 tw-text-white tw-border-blue-600'
                                                : 'tw-bg-white tw-text-gray-700 tw-border-gray-300 hover:tw-border-gray-400'
                                        }`}
                                        disabled={isUploading}
                                    >
                                        Enter URL
                                    </button>
                                </div>

                                {/* File Upload Input */}
                                {uploadMethod === 'file' && (
                                    <div className="tw-mb-3">
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp,image/gif"
                                            onChange={handleFileSelect}
                                            disabled={isUploading}
                                            className="tw-hidden"
                                        />
                                        <button
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={isUploading}
                                            className={`tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-rounded tw-border-2 tw-border-dashed tw-transition-colors ${
                                                isUploading
                                                    ? 'tw-bg-gray-100 tw-text-gray-400 tw-border-gray-300 tw-cursor-not-allowed'
                                                    : 'tw-bg-white tw-text-blue-600 tw-border-blue-300 hover:tw-border-blue-500 hover:tw-bg-blue-50 tw-cursor-pointer'
                                            }`}
                                        >
                                            {isUploading ? 'Uploading...' : 'Click to select image'}
                                        </button>
                                    </div>
                                )}

                                {/* URL Input */}
                                {uploadMethod === 'url' && (
                                    <div className="tw-mb-3">
                                        <input
                                            type="text"
                                            value={imageUrl}
                                            onChange={(e) => handleUrlInput(e.target.value)}
                                            placeholder="Enter image URL"
                                            disabled={isUploading}
                                            className={`${styles.input} tw-text-sm`}
                                        />
                                    </div>
                                )}

                                {/* Error Message */}
                                {uploadError && (
                                    <div className="tw-mb-3 tw-p-2 tw-bg-red-100 tw-border tw-border-red-300 tw-text-red-700 tw-text-sm tw-rounded">
                                        {uploadError}
                                    </div>
                                )}

                                {/* Image Preview */}
                                {imageUrl && (
                                    <div className="tw-mb-3">
                                        <div className="tw-relative tw-inline-block">
                                            <img
                                                src={imageUrl}
                                                alt="Category preview"
                                                className="tw-max-w-[120px] tw-max-h-[120px] tw-rounded tw-border tw-border-gray-200"
                                                onError={(e) => {
                                                    console.error('Image load error:', imageUrl);
                                                    showError('Failed to load image. Check the URL.');
                                                }}
                                            />
                                            <button
                                                onClick={removeImage}
                                                disabled={isUploading}
                                                className="tw-absolute tw-top-1 tw-right-1 tw-bg-red-600 hover:tw-bg-red-700 tw-text-white tw-rounded-full tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center tw-text-sm tw-font-bold tw-transition-colors"
                                                title="Remove image"
                                            >
                                                <svg className="tw-w-4 tw-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Info text */}
                                <div className="tw-text-xs tw-text-gray-500">
                                    Max file size: 5MB. Supported formats: JPEG, PNG, WebP, GIF
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Show Sort by Attributes */}
                    <div className={styles.section}>
                        <div className={styles.checkboxContainer}>
                            <input
                                type="checkbox"
                                checked={showSortByAttributes}
                                onChange={(e) => onConfigChange('showSortByAttributes', e.target.checked)}
                                className={styles.checkboxInput}
                            />
                            <label className={styles.checkboxLabel}>
                                Show 'Sort by Attributes'
                            </label>
                            {/* <div className="tw-ml-2">
                                <OverlayIcon message={''} />
                            </div> */}
                        </div>
                        
                        {showSortByAttributes && (
                            <div className={styles.nestedContainer}>
                                <div className={styles.nestedInputContainer}>
                                    <button
                                        onClick={handleAddAttributes}
                                        className="tw-text-blue-600 tw-text-sm tw-font-medium hover:tw-underline tw-bg-transparent tw-border-none tw-p-0"
                                    >
                                        + Add Attributes
                                    </button>
                                    {effectiveSortableAttributes.length > 0 && (
                                        <div className="tw-mt-2 tw-space-y-2">
                                            {effectiveSortableAttributes.map((attr, index) => (
                                                <div 
                                                    key={attr.id || index} 
                                                    className="tw-flex tw-items-center tw-px-4 tw-py-1 tw-bg-white tw-border tw-border-[#D1D1D6] tw-rounded"
                                                >
                                                    <span className="tw-text-sm tw-font-medium tw-text-[#172B4D] tw-flex-1 tw-truncate tw-max-w-[150px]"
                                                        title={attr.label || attr.name}>
                                                        {attr.label || attr.name}
                                                    </span>
                                                    
                                                    {/* Edit (pencil) icon */}
                                                    <button 
                                                        className="tw-p-1 tw-text-[#6B778C] hover:tw-text-[#172B4D] tw-transition-colors"
                                                        onClick={() => handleAddAttributes()}
                                                        title="Edit attribute"
                                                    >
                                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                                            <path d="M11.013 1.427a1.75 1.75 0 012.474 0l1.086 1.086a1.75 1.75 0 010 2.474l-8.61 8.61c-.21.21-.47.364-.756.445l-3.251.93a.75.75 0 01-.927-.928l.929-3.25a1.75 1.75 0 01.445-.758l8.61-8.61zm1.414 1.06a.25.25 0 00-.354 0L10.811 3.75l1.439 1.44 1.263-1.263a.25.25 0 000-.354l-1.086-1.086zM11.189 6.25L9.75 4.81l-6.286 6.287a.25.25 0 00-.064.108l-.558 1.953 1.953-.558a.25.25 0 00.108-.064l6.286-6.286z"/>
                                                        </svg>
                                                    </button>
                                                    
                                                    {/* Vertical divider */}
                                                    <div className="tw-w-px tw-h-4 tw-bg-[#D1D1D6] tw-mx-3"></div>
                                                    
                                                    {/* Data type */}
                                                    <span className="tw-text-sm tw-text-[#6B778C] tw-font-normal">
                                                        {attr.dataType === 'TEXT' ? 'TEXT' : attr.dataType === 'NUMBER' ? 'Numeric' : attr.dataType}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* Show In Stock */}
                    <div className={styles.section}>
                        <div className={styles.checkboxContainer}>
                            <input
                                type="checkbox"
                                checked={showInStock}
                                onChange={(e) => onConfigChange('showInStock', e.target.checked)}
                                className={styles.checkboxInput}
                            />
                            <label className={styles.checkboxLabel}>{`Show 'In Stock'`}</label>
                        </div>
                        
                        {showInStock && (
                            <div className={styles.nestedContainer}>
                                <div className={styles.nestedInputContainer}>
                                    <label className={styles.inputLabel}>Filter Label Text</label>
                                    <input
                                        type="text"
                                        value={localInStockText}
                                        onChange={(e) => handleDebouncedInStockChange(e.target.value)}
                                        className={styles.input}
                                        placeholder="Show In Stock only"
                                    />
                                    
                                    {/* Select by default option - only visible when Show 'In Stock' is enabled */}
                                    <div className="tw-mt-3">
                                        <div className={styles.checkboxContainer}>
                                            <input
                                                type="checkbox"
                                                checked={selectByDefault}
                                                onChange={(e) => onConfigChange('selectByDefault', e.target.checked)}
                                                className={styles.checkboxInput}
                                            />
                                            <label className={styles.checkboxLabel}>Select by default</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* Hide category if no products */}
                    <div className={styles.section}>
                        <div className={styles.checkboxContainer}>
                            <input
                                type="checkbox"
                                checked={hideIfNoProducts}
                                onChange={(e) => onConfigChange('hideIfNoProducts', e.target.checked)}
                                className={styles.checkboxInput}
                            />
                            
                            <div className="tw-flex tw-items-center">
                                <label className='tw-color-[#6D788C] tw-text-sm'>
                                    Hide category if 'no' products are{' '}
                                    <span className="tw-inline-flex tw-items-center tw-gap-1">
                                        available
                                        <OverlayIcon message={'When enabled, automatically hides the sections (Page/SubModel/Category) if no compatible products are available'} />
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Sort by Attributes Modal using PopUp component */}
            {showAttributeModal && (
                <PopUp 
                    title="Add Sort by Attributes"
                    content={
                        <AddAttributesPopup
                            attributes={attributes}
                            existingAttributes={effectiveSortableAttributes}
                            onClose={() => setShowAttributeModal(false)}
                            onSave={handleSaveAttributes}
                        />
                    }
                />
            )}
        </div>
    );
};